import React from 'react';

// Keep hair anchored consistently across all head shapes.
// Longer faces place the mouth slightly lower.


// Shared measurements for facial features, hair, neck and clothing.
// Clothing offsets are starting values to verify during visual testing.
export const HEAD_LAYOUTS = {
  circle: {
    centerX: 200,
    eyeY: 183,
    mouthY: 231,
    hairY: 102,
    clothingOffsetY: 0,
    neckY: 252,
    neckHeight: 66,
    neckShadowOffsetY: 0,
  },

  square: {
    centerX: 200,
    eyeY: 183,
    mouthY: 231,
    hairY: 102,
    clothingOffsetY: 0,
    neckY: 252,
    neckHeight: 66,
    neckShadowOffsetY: 0,
  },

  oval: {
    centerX: 200,
    eyeY: 185,
    mouthY: 239,
    hairY: 102,
    clothingOffsetY: 22,
    neckY: 252,
    neckHeight: 88,
    neckShadowOffsetY: 22,
  },

  heart: {
    centerX: 200,
    eyeY: 181,
    mouthY: 229,
    hairY: 102,
    clothingOffsetY: 12,
    neckY: 252,
    neckHeight: 78,
    neckShadowOffsetY: 12,
  },

  diamond: {
    centerX: 200,
    eyeY: 183,
    mouthY: 233,
    hairY: 102,
    clothingOffsetY: 14,
    neckY: 252,
    neckHeight: 80,
    neckShadowOffsetY: 14,
  },

  oblong: {
    centerX: 200,
    eyeY: 188,
    mouthY: 245,
    hairY: 102,
    clothingOffsetY: 36,
    neckY: 252,
    neckHeight: 102,
    neckShadowOffsetY: 36,
  },
};




// New shapes share a rounded crown compatible with existing hair.
// Differences appear mainly in the cheeks, jaw and chin.
const HEAD_PATHS = {
  oval: `
    M200 100
    C249 100 285 138 285 185
    C285 225 266 259 239 280
    C226 290 214 296 200 296
    C186 296 174 290 161 280
    C134 259 115 225 115 185
    C115 138 151 100 200 100
    Z
  `,

  heart: `
    M200 100
    C249 100 285 138 285 183
    C285 211 267 235 245 252
    C226 267 215 282 200 286
    C185 282 174 267 155 252
    C133 235 115 211 115 183
    C115 138 151 100 200 100
    Z
  `,

  diamond: `
    M200 100
    C249 100 282 137 280 171
    C280 180 290 191 290 202
    C290 217 269 235 249 250
    C229 265 216 282 200 288
    C184 282 171 265 151 250
    C131 235 110 217 110 202
    C110 191 120 180 120 171
    C118 137 151 100 200 100
    Z
  `,

  oblong: `
    M200 100
    C249 100 285 138 285 181
    L283 231
    C282 262 266 285 241 297
    C228 304 214 307 200 307
    C186 307 172 304 159 297
    C134 285 118 262 117 231
    L115 181
    C115 138 151 100 200 100
    Z
  `,
};


// Returns one SVG shape directly so it can also be used inside a clipPath.
// Extra props allow callers to supply fill or a coordinate transform.
export function HeadSilhouette({
  characterType = 'human_male',
  shape = 'circle',
  ...svgProps
}) {
  const isFemale = characterType === 'human_female';
  const isSquare = shape === 'square';
  const newHeadPath = HEAD_PATHS[shape];

  if (newHeadPath) {
    return <path {...svgProps} d={newHeadPath} />;
  }

  if (isFemale) {
    return (
      <path
        {...svgProps}
        d={
          isSquare
            ? `
              M166 102
              H234
              Q282 102 282 150
              V211
              Q282 247 253 263
              Q230 278 200 278
              Q170 278 147 263
              Q118 247 118 211
              V150
              Q118 102 166 102
              Z
            `
            : `
              M200 100
              C249 100 285 138 285 185
              C285 222 265 252 238 268
              Q200 290 162 268
              C135 252 115 222 115 185
              C115 138 151 100 200 100
              Z
            `
        }
      />
    );
  }

  if (isSquare) {
    return (
      <rect
        {...svgProps}
        x="118"
        y="102"
        width="164"
        height="174"
        rx="48"
      />
    );
  }

  return (
    <ellipse
      {...svgProps}
      cx="200"
      cy="189"
      rx="85"
      ry="89"
    />
  );
}



export default function AvatarHead({
  characterType = 'human_male',
  shape = 'circle',
  skin,
}) {


  const layout = HEAD_LAYOUTS[shape] || HEAD_LAYOUTS.circle;


  return (
    <g>


      {/* Neck measurements follow the selected head layout. */}
      <rect
        x="174"
        y={layout.neckY}
        width="52"
        height={layout.neckHeight}
        rx="22"
        fill={skin.color}
      />

      <path
        d="M174 266 Q200 286 226 266 L226 284 Q200 301 174 284 Z"
        transform={
          layout.neckShadowOffsetY === 0
            ? undefined
            : `translate(0 ${layout.neckShadowOffsetY})`
        }
        fill={skin.shadow}
      />

      {/* Shared ears retain their existing attachment positions. */}
      <ellipse
        cx="112"
        cy="195"
        rx="17"
        ry="23"
        fill={skin.color}
      />

      <ellipse
        cx="288"
        cy="195"
        rx="17"
        ry="23"
        fill={skin.color}
      />

      <path
        d="M111 186 Q101 195 112 204"
        fill="none"
        stroke={skin.shadow}
        strokeWidth="5"
        strokeLinecap="round"
      />

      <path
        d="M289 186 Q299 195 288 204"
        fill="none"
        stroke={skin.shadow}
        strokeWidth="5"
        strokeLinecap="round"
      />




      <HeadSilhouette
        characterType={characterType}
        shape={shape}
        fill={skin.color}
      />



    </g>
  );
}
